/**
 * UserDeletionModal Component
 * Two-stage confirmation modal for permanent user account deletion
 * Stage 1: Deletion impact preview
 * Stage 2: Reason input with explicit confirmation checkbox
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { UserProfile } from '../../lib/types/user';
import { useDeleteUser } from '../../hooks/useAdminApi';
import { UserAvatar } from '../ui/UserAvatar';
import { FormButton } from '../form/FormButton';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { cn } from '../../lib/utils';

interface UserDeletionModalProps {
  user: UserProfile;
  isOpen: boolean;
  onClose: () => void;
}

// Zod schema for deletion form validation
const deletionSchema = z.object({
  reason: z
    .string()
    .min(10, 'Reason must be at least 10 characters')
    .max(500, 'Reason must be less than 500 characters')
    .refine(val => val.trim().length >= 10, {
      message: 'Reason must contain at least 10 non-whitespace characters',
    }),
  confirmDeletion: z.boolean().refine(val => val === true, {
    message: 'You must confirm that you understand this action is permanent',
  }),
});

type DeletionForm = z.infer<typeof deletionSchema>;

export function UserDeletionModal({
  user,
  isOpen,
  onClose,
}: UserDeletionModalProps) {
  const deleteUser = useDeleteUser();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<DeletionForm>({
    resolver: zodResolver(deletionSchema),
    mode: 'onChange',
    defaultValues: {
      reason: '',
      confirmDeletion: false,
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset();
      setShowConfirmation(false);
    }
  }, [isOpen, reset]);

  // Handle proceeding from Stage 1 to Stage 2
  const handleProceed = () => {
    setShowConfirmation(true);
  };

  // Handle going back from Stage 2 to Stage 1
  const handleBack = () => {
    setShowConfirmation(false);
  };

  // Handle modal close
  const handleClose = () => {
    if (deleteUser.isPending) return;
    reset();
    setShowConfirmation(false);
    onClose();
  };

  // Handle form submission
  const onSubmit = async (data: DeletionForm) => {
    try {
      await deleteUser.mutateAsync({
        uid: user.uid,
        reason: data.reason.trim(),
      });
      handleClose();
    } catch (_error) {
      // Error is handled by the mutation and displayed via AnimatedMessage
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={deleteUser.isPending ? undefined : handleClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Delete User Account
            </h3>
            <button
              onClick={handleClose}
              disabled={deleteUser.isPending}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <UserAvatar
              displayName={user.display_name}
              photoUrls={user.photo_url}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.display_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user.uid}
              </p>
            </div>
          </div>

          {/* Stage 1: Deletion Impact Preview */}
          {!showConfirmation ? (
            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="flex">
                  <svg
                    className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Permanent Deletion Warning
                    </h4>
                    <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                      This action will permanently delete{' '}
                      <strong>{user.display_name}</strong>&apos;s account and
                      all associated data. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Deletion Impact Details */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  The following data will be deleted:
                </h4>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span>
                      <strong>Firestore documents:</strong> User profile,
                      badges, subscriptions, and related data
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <span>
                      <strong>Stripe subscriptions:</strong> Active
                      subscriptions will be canceled and customer data
                      anonymized
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>
                      <strong>Storage files:</strong> Profile photos and
                      uploaded content
                    </span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={deleteUser.isPending}
                  className="flex-1"
                >
                  Cancel
                </FormButton>
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={handleProceed}
                  disabled={deleteUser.isPending}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Proceed to Confirmation
                </FormButton>
              </div>
            </div>
          ) : (
            /* Stage 2: Reason Input with Confirmation */
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Reason for Deletion */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Deletion *
                </label>
                <textarea
                  {...register('reason')}
                  rows={4}
                  placeholder="Explain why this user account is being deleted... (10-500 characters)"
                  className={cn(
                    'block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-red-500 focus:ring-red-500 sm:text-sm',
                    errors.reason &&
                      'border-red-300 focus:border-red-500 focus:ring-red-500'
                  )}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.reason.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This reason will be logged for audit trail purposes.
                </p>
              </div>

              {/* Confirmation Checkbox */}
              <div>
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    {...register('confirmDeletion')}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    I understand this action is permanent and cannot be undone.
                    All user data will be permanently deleted.
                  </span>
                </label>
                {errors.confirmDeletion && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.confirmDeletion.message}
                  </p>
                )}
              </div>

              {/* Error Message */}
              {deleteUser.error && (
                <AnimatedMessage
                  type="error"
                  message={deleteUser.error.message}
                />
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <FormButton
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={deleteUser.isPending}
                  className="flex-1"
                >
                  Back
                </FormButton>
                <FormButton
                  type="submit"
                  variant="primary"
                  disabled={!isValid || deleteUser.isPending}
                  isLoading={deleteUser.isPending}
                  loadingText="Deleting..."
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:hover:bg-gray-300"
                >
                  Delete User Account
                </FormButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
